import { Fragment } from "react";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { ErrorText, RadioGroup, TrashIcon } from "~/common/components";
import Input from "~/common/components/ui/Input";
import TextArea from "~/common/components/ui/Textarea";
import {
  choicesModeOptions,
  EMPTY_CHOICE,
  MAXIMUM_QUESTION_CHOICES,
  QUESTION_TEXT_MAX_CHARACTERS,
} from "~/providers/constants/supplementalQuestions";
import { AddMultipleChoiceQuestionForm } from "~/providers/types/supplementalQuestions";

export const MultipleChoiceQuestionFields = () => {
  const {
    register,
    watch,
    control,
    formState: { errors },
    trigger,
  } = useFormContext<AddMultipleChoiceQuestionForm>();
  const { fields, append, remove } = useFieldArray({ name: "choices" });

  const choicesNotEmpty = fields.length >= 1;
  const hasMaxChoices = fields.length >= MAXIMUM_QUESTION_CHOICES;

  return (
    <Fragment>
      <TextArea
        label="Question text"
        rows={5}
        {...register("text")}
        charactersCount={watch("text")?.length}
        maxCharacters={QUESTION_TEXT_MAX_CHARACTERS}
        error={errors.text?.message}
      />

      <div className="flex flex-col gap-3">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-sm font-medium">Choices</p>
            <p className="text-sm text-gray-60">{`Max of ${MAXIMUM_QUESTION_CHOICES} choices`}</p>
          </div>
          <Controller
            name="selectionMethod"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                className="flex flex-row items-center gap-3 text-sm"
                options={choicesModeOptions}
              />
            )}
          />
        </div>

        {fields.map((field, index) => (
          <div key={field.id} className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <div className="flex flex-1 items-center rounded-md border border-gray-20">
                <div className="flex w-10 items-center justify-center self-stretch rounded-l-md border-r border-gray-20 bg-primary-50 text-sm text-gray-70">
                  {index + 1}
                </div>
                <Input
                  placeholder="Write answer here"
                  inputClassName="rounded-none border-none"
                  containerClassName="rounded-none border-none"
                  className="rounded-l-none rounded-r-md border-none"
                  isError={!!errors.choices?.[index]}
                  {...register(`choices.${index}.label` as const, {
                    onChange: () => {
                      void trigger("choices");
                    },
                  })}
                />
              </div>
              {fields.length > 2 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  disabled={fields.length <= 1}
                >
                  <TrashIcon className="size-5" />
                </button>
              )}
            </div>
            {errors.choices?.[index]?.label?.message && (
              <ErrorText>{errors.choices?.[index]?.label.message}</ErrorText>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={() => append(EMPTY_CHOICE)}
          disabled={hasMaxChoices}
          className={twMerge(
            "flex items-center justify-center gap-2 rounded border border-dashed p-2.5",
            hasMaxChoices && "cursor-not-allowed bg-gray-200",
            choicesNotEmpty && fields.length > 2 && "mr-8",
          )}
        >
          <PlusCircleIcon className="size-5" />
          <span className="text-sm text-gray-10">Add answer</span>
        </button>

        {errors.choices?.message && (
          <ErrorText>{errors.choices?.message}</ErrorText>
        )}
      </div>
    </Fragment>
  );
};
